import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Plus, Truck, Package, MapPin, Clock, Phone, Mail, User,
  FileText, Trash2, Edit3, ArrowRight, Check, RefreshCw, Loader2, X, Copy,
  Key, Camera, Image, Map
} from 'lucide-react';

const DeliveryMap = lazy(() => import('../components/DeliveryMap'));
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'booked', label: 'Booked', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  { key: 'dispatched', label: 'Dispatched', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20' },
  { key: 'picked_up', label: 'Picked Up', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
  { key: 'in_transit', label: 'In Transit', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20' },
  { key: 'arriving', label: 'Arriving', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
  { key: 'delivered', label: 'Delivered', color: 'text-green-400 bg-green-400/10 border-green-400/20' },
];

const getStageColor = (status) => STAGES.find(s => s.key === status)?.color || 'text-slate-400 bg-slate-400/10 border-slate-400/20';
const getStageLabel = (status) => STAGES.find(s => s.key === status)?.label || status;

const emptyForm = {
  custom_id: '', customer_name: '', customer_phone: '', customer_email: '',
  pickup_address: '', delivery_address: '', eta: '',
  driver_name: '', driver_phone: '', show_driver_contact: false,
  notes: '', internal_notes: '',
};

const AdminDeliveries = ({ convertQuote, onClearConvert }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [mapOpenId, setMapOpenId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [delRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/deliveries`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setDeliveries(delRes.data);
      setStats(statsRes.data.deliveries || {});
    } catch (error) {
      console.error('Failed to fetch delivery data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Handle convert from quote
  useEffect(() => {
    if (convertQuote) {
      setForm({
        ...emptyForm,
        customer_name: convertQuote.name || '',
        customer_phone: convertQuote.phone || '',
        customer_email: convertQuote.email || '',
        pickup_address: convertQuote.pickup_location || '',
        delivery_address: convertQuote.delivery_location || '',
        notes: convertQuote.load_details || '',
      });
      setEditingId(null);
      setDialogOpen(true);
    }
  }, [convertQuote]);

  const openCreateDialog = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (delivery) => {
    setForm({
      custom_id: delivery.id,
      customer_name: delivery.customer_name || '',
      customer_phone: delivery.customer_phone || '',
      customer_email: delivery.customer_email || '',
      pickup_address: delivery.pickup_address || '',
      delivery_address: delivery.delivery_address || '',
      eta: delivery.eta ? delivery.eta.slice(0, 16) : '',
      driver_name: delivery.driver_name || '',
      driver_phone: delivery.driver_phone || '',
      show_driver_contact: delivery.show_driver_contact || false,
      notes: delivery.notes || '',
      internal_notes: delivery.internal_notes || '',
    });
    setEditingId(delivery.id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    if (convertQuote) onClearConvert?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        const { custom_id, ...updateData } = form;
        // Convert empty strings to null for optional fields
        const cleaned = {};
        for (const [k, v] of Object.entries(updateData)) {
          if (v !== '' && v !== null) cleaned[k] = v;
        }
        if (updateData.eta === '') cleaned.eta = null;
        await axios.put(`${API}/admin/deliveries/${editingId}`, cleaned, { withCredentials: true });
      } else if (convertQuote) {
        await axios.post(`${API}/admin/deliveries/from-quote/${convertQuote.id}`, {}, { withCredentials: true });
      } else {
        const payload = { ...form };
        if (!payload.custom_id) delete payload.custom_id;
        if (!payload.eta) delete payload.eta;
        await axios.post(`${API}/admin/deliveries`, payload, { withCredentials: true });
      }
      handleDialogClose();
      fetchData();
    } catch (error) {
      console.error('Failed to save delivery:', error);
      alert(error.response?.data?.detail || 'Failed to save delivery');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (jobId, newStatus) => {
    try {
      await axios.put(`${API}/admin/deliveries/${jobId}/status`, { status: newStatus, note: statusNote }, { withCredentials: true });
      setStatusNote('');
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteDelivery = async (jobId) => {
    try {
      await axios.delete(`${API}/admin/deliveries/${jobId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to delete delivery:', error);
    }
  };

  const copyJobId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredDeliveries = filter === 'all' ? deliveries : deliveries.filter(d => d.status === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const stageIdx = (status) => STAGES.findIndex(s => s.key === status);

  return (
    <div data-testid="admin-deliveries">
      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8"
      >
        {[
          { key: 'total', label: 'Total', color: 'text-white', icon: Truck },
          ...STAGES.map(s => ({ key: s.key, label: s.label, color: s.color.split(' ')[0], icon: Truck })),
        ].map(item => (
          <div key={item.key} className="bg-brand-surface rounded-xl border border-white/10 p-4" data-testid={`del-stat-${item.key}`}>
            <span className="text-slate-400 text-xs">{item.label}</span>
            <p className={`text-2xl font-heading ${item.color}`}>{stats[item.key] || 0}</p>
          </div>
        ))}
      </motion.div>

      {/* Header + Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="font-heading text-2xl text-white">
          DELIVERY <span className="text-brand-red">JOBS</span>
        </h2>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="delivery-filter-select">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-brand-surface border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">All</SelectItem>
              {STAGES.map(s => (
                <SelectItem key={s.key} value={s.key} className="text-white hover:bg-white/10">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={openCreateDialog}
            className="flex items-center gap-2 px-5 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg font-heading text-sm transition-all"
            data-testid="create-delivery-button"
          >
            <Plus className="w-4 h-4" />
            NEW DELIVERY
          </button>
        </div>
      </div>

      {/* Deliveries List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading deliveries...</div>
      ) : filteredDeliveries.length === 0 ? (
        <div className="text-center py-12 bg-brand-surface rounded-xl border border-white/10">
          <Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No delivery jobs found</p>
          <button onClick={openCreateDialog} className="mt-4 text-brand-red hover:text-red-400 text-sm">Create your first delivery</button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {filteredDeliveries.map((del, index) => (
            <motion.div
              key={del.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-brand-surface rounded-xl border border-white/10 p-6"
              data-testid={`delivery-card-${del.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-grow min-w-0">
                  {/* Header row */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <button
                      onClick={() => copyJobId(del.id)}
                      className="font-heading text-lg text-brand-red hover:text-red-400 transition-colors flex items-center gap-1.5"
                      title="Copy Job ID"
                      data-testid={`copy-job-id-${del.id}`}
                    >
                      {del.id}
                      {copiedId === del.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 opacity-50" />}
                    </button>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStageColor(del.status)}`}>
                      {getStageLabel(del.status)}
                    </span>
                    <span className="text-white font-medium">{del.customer_name}</span>
                    {del.quote_id && <span className="text-xs text-slate-500 bg-white/5 px-2 py-0.5 rounded">From quote</span>}
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Phone className="w-4 h-4 text-brand-red flex-shrink-0" />
                      <a href={`tel:${del.customer_phone}`} className="hover:text-white truncate">{del.customer_phone}</a>
                    </div>
                    {del.customer_email && (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Mail className="w-4 h-4 text-brand-blue flex-shrink-0" />
                        <a href={`mailto:${del.customer_email}`} className="hover:text-white truncate">{del.customer_email}</a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-400">
                      <Clock className="w-4 h-4 text-slate-500 flex-shrink-0" />
                      <span className="truncate">{formatDate(del.created_at)}</span>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs text-slate-500">Pickup</span>
                        <p className="text-slate-300 text-sm truncate">{del.pickup_address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-brand-red mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs text-slate-500">Delivery</span>
                        <p className="text-slate-300 text-sm truncate">{del.delivery_address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Driver + ETA + PIN row */}
                  <div className="flex flex-wrap gap-4 mt-3 text-sm">
                    {del.driver_name && (
                      <div className="flex items-center gap-2 text-slate-400">
                        <User className="w-4 h-4 text-purple-400" />
                        <span>{del.driver_name}</span>
                      </div>
                    )}
                    {del.eta && (
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <span>ETA: {formatDate(del.eta)}</span>
                      </div>
                    )}
                    {del.driver_pin && (
                      <div className="flex items-center gap-2 text-slate-400" data-testid={`driver-pin-${del.id}`}>
                        <Key className="w-4 h-4 text-yellow-400" />
                        <span>PIN: <span className="text-yellow-400 font-mono font-bold">{del.driver_pin}</span></span>
                      </div>
                    )}
                    {del.pod_photo_url && (
                      <div className="flex items-center gap-2 text-green-400">
                        <Camera className="w-4 h-4" />
                        <span className="text-xs">POD Photo</span>
                      </div>
                    )}
                    {del.pod_signature_url && (
                      <div className="flex items-center gap-2 text-green-400">
                        <FileText className="w-4 h-4" />
                        <span className="text-xs">Signature</span>
                      </div>
                    )}
                  </div>

                  {/* Mini progress bar */}
                  <div className="flex items-center gap-1 mt-3">
                    {STAGES.map((s, i) => (
                      <div
                        key={s.key}
                        className={`h-1.5 flex-1 rounded-full ${i <= stageIdx(del.status) ? 'bg-brand-red' : 'bg-white/10'}`}
                      />
                    ))}
                  </div>
                  {/* Map toggle + display */}
                  {del.driver_lat && del.driver_lng && (
                    <div className="mt-3">
                      <button onClick={() => setMapOpenId(mapOpenId === del.id ? null : del.id)}
                        className="flex items-center gap-1.5 text-xs text-brand-blue hover:text-blue-300 transition-colors"
                        data-testid={`map-toggle-${del.id}`}>
                        <Map className="w-3.5 h-3.5" /> {mapOpenId === del.id ? 'Hide Map' : 'Show Map'}
                      </button>
                      {mapOpenId === del.id && (
                        <div className="mt-2" data-testid={`admin-map-${del.id}`}>
                          <Suspense fallback={<div className="h-[200px] bg-white/5 rounded-xl" />}>
                            <DeliveryMap driverLat={del.driver_lat} driverLng={del.driver_lng}
                              locationUpdatedAt={del.location_updated_at} height="200px" />
                          </Suspense>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                  <Select value={del.status} onValueChange={(val) => updateStatus(del.id, val)}>
                    <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white" data-testid={`del-status-select-${del.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-surface border-white/10">
                      {STAGES.map(s => (
                        <SelectItem key={s.key} value={s.key} className={`${s.color.split(' ')[0]} hover:bg-white/10`}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => openEditDialog(del)}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:text-white transition-colors"
                    data-testid={`edit-delivery-${del.id}`}
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                        data-testid={`delete-delivery-${del.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-brand-surface border-white/10">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Delivery {del.id}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          This will permanently delete this delivery job. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteDelivery(del.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid={`confirm-delete-delivery-${del.id}`}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); }}>
        <DialogContent className="bg-brand-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              {editingId ? `EDIT ${editingId}` : convertQuote ? 'CONVERT QUOTE TO DELIVERY' : 'CREATE NEW DELIVERY'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingId ? 'Update delivery job details.' : 'Fill in the details to create a new delivery job.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            {/* Custom Job ID (create only) */}
            {!editingId && !convertQuote && (
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Custom Job ID (optional, auto-generated if blank)</label>
                <input
                  type="text"
                  value={form.custom_id}
                  onChange={(e) => setForm(f => ({ ...f, custom_id: e.target.value }))}
                  placeholder="e.g. TF-12345"
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-custom-id"
                />
              </div>
            )}

            {/* Customer Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Customer Name *</label>
                <input
                  type="text" required value={form.customer_name}
                  onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-customer-name"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Customer Phone *</label>
                <input
                  type="text" required value={form.customer_phone}
                  onChange={(e) => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-customer-phone"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Customer Email</label>
              <input
                type="email" value={form.customer_email}
                onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))}
                placeholder="For delivery status notifications"
                className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                data-testid="delivery-customer-email"
              />
            </div>

            {/* Addresses */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Pickup Address *</label>
                <input
                  type="text" required value={form.pickup_address}
                  onChange={(e) => setForm(f => ({ ...f, pickup_address: e.target.value }))}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-pickup-address"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Delivery Address *</label>
                <input
                  type="text" required value={form.delivery_address}
                  onChange={(e) => setForm(f => ({ ...f, delivery_address: e.target.value }))}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-delivery-address"
                />
              </div>
            </div>

            {/* ETA */}
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Estimated Arrival (ETA)</label>
              <input
                type="datetime-local" value={form.eta}
                onChange={(e) => setForm(f => ({ ...f, eta: e.target.value }))}
                className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-red outline-none [color-scheme:dark]"
                data-testid="delivery-eta"
              />
            </div>

            {/* Driver Info */}
            <div className="border-t border-white/10 pt-4">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Driver Assignment</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <input
                  type="text" value={form.driver_name}
                  onChange={(e) => setForm(f => ({ ...f, driver_name: e.target.value }))}
                  placeholder="Driver Name"
                  className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-driver-name"
                />
                <input
                  type="text" value={form.driver_phone}
                  onChange={(e) => setForm(f => ({ ...f, driver_phone: e.target.value }))}
                  placeholder="Driver Phone"
                  className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none"
                  data-testid="delivery-driver-phone"
                />
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Switch
                  checked={form.show_driver_contact}
                  onCheckedChange={(v) => setForm(f => ({ ...f, show_driver_contact: v }))}
                  className="data-[state=checked]:bg-brand-red"
                  data-testid="delivery-show-driver-toggle"
                />
                <span className="text-sm text-slate-400">Show driver contact to customer</span>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Customer Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Visible to customer on tracking page"
                  rows={3}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none resize-none"
                  data-testid="delivery-notes"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Internal Notes</label>
                <textarea
                  value={form.internal_notes}
                  onChange={(e) => setForm(f => ({ ...f, internal_notes: e.target.value }))}
                  placeholder="Admin only, not visible to customer"
                  rows={3}
                  className="mt-1 w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none resize-none"
                  data-testid="delivery-internal-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <button type="button" onClick={handleDialogClose} className="px-5 py-2.5 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10">
                Cancel
              </button>
              <button
                type="submit" disabled={saving}
                className="px-5 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg font-heading text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="delivery-submit-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingId ? 'UPDATE' : 'CREATE DELIVERY'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDeliveries;
